import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  NoteApiError,
  NoteHistoryResponse,
  NoteResponse,
  NotesAPI,
} from "../services/notes.service";
import { useNoteEditPolicy } from "../hooks/useNoteEditPolicy";
import { useRole } from "../hooks/useRole";
import { OnboardingAPI } from "../services/onboarding.service";
import { AuthorChip } from "./ui/AuthorChip";

// ─── Icons ────────────────────────────────────────────────────────────────────

const IcNotebook = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
  </svg>
);

const IcPlus = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const IcPencil = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const IcHistory = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M3 3v5h5" /><path d="M3.05 13A9 9 0 1 0 6 5.3L3 8" /><path d="M12 7v5l4 2" />
  </svg>
);

const IcTrash = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

// ─── Formatting ───────────────────────────────────────────────────────────────

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  const date = d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  const time = d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  return `${date}, ${time}`;
}

// ─── Note form (add / edit) ───────────────────────────────────────────────────

interface NoteFormProps {
  initialBody: string;
  onSave: (body: string) => void;
  onCancel: () => void;
  saving: boolean;
  error: string | null;
}

const NoteForm: React.FC<NoteFormProps> = ({ initialBody, onSave, onCancel, saving, error }) => {
  const [body, setBody] = useState(initialBody);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!body.trim()) return;
    onSave(body.trim());
  };

  return (
    <form onSubmit={handleSubmit} className="mt-3 flex flex-col gap-2.5 bg-slate-50 rounded-2xl border border-slate-100 p-3">
      <textarea
        value={body}
        onChange={e => setBody(e.target.value)}
        placeholder="Add a note…"
        required
        autoFocus
        rows={3}
        className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-200 resize-y"
      />
      {error && <p className="text-xs text-red-500">{error}</p>}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 rounded-xl text-sm font-semibold text-slate-500 hover:bg-slate-100 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving || !body.trim()}
          className="px-4 py-1.5 rounded-xl text-white text-sm font-semibold shadow-sm hover:opacity-90 transition-opacity bg-gradient-to-br from-blue-500 to-sky-400 disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </form>
  );
};

// ─── History popover ──────────────────────────────────────────────────────────

interface HistoryPopoverProps {
  history: NoteHistoryResponse | null;
  loading: boolean;
  onClose: () => void;
}

const HistoryPopover: React.FC<HistoryPopoverProps> = ({ history, loading, onClose }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  return (
    <div
      ref={ref}
      className="absolute z-50 top-full right-0 mt-1 w-80 max-h-80 overflow-y-auto bg-white border border-slate-200 rounded-xl shadow-lg p-3"
    >
      <p className="text-xs font-semibold text-slate-500 mb-2">Version history</p>
      {loading && <p className="text-xs text-slate-400 py-2">Loading…</p>}
      {!loading && history?.versions.map((v, i) => (
        <div key={v.note_id} className={i < history.versions.length - 1 ? "border-b border-slate-100 py-2" : "py-2"}>
          <div className="flex items-center justify-between gap-2 mb-1">
            <AuthorChip name={v.author_name} />
            <span className="text-[10px] text-slate-400 shrink-0">{formatTimestamp(v.created_at)}</span>
          </div>
          <p className="text-xs text-slate-600 whitespace-pre-wrap">{v.body}</p>
        </div>
      ))}
    </div>
  );
};

// ─── Note row ─────────────────────────────────────────────────────────────────

interface NoteRowProps {
  note: NoteResponse;
  canEdit: boolean;
  onEdit: () => void;
  onDelete: () => void;
  isDeleting: boolean;
}

const NoteRow: React.FC<NoteRowProps> = ({ note, canEdit, onEdit, onDelete, isDeleting }) => {
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<NoteHistoryResponse | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  const openHistory = async () => {
    setHistoryOpen(true);
    if (history) return;
    setHistoryLoading(true);
    try {
      const data = await NotesAPI.history(note.patient_id, note.entry_id);
      setHistory(data);
    } catch {
      setHistory({ entry_id: note.entry_id, versions: [] });
    } finally {
      setHistoryLoading(false);
    }
  };

  return (
    <div className="py-3 border-b border-slate-100 last:border-0">
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-2">
          <AuthorChip name={note.author_name} />
          <span className="text-[11px] text-slate-400">{formatTimestamp(note.entry_created_at)}</span>
        </div>
        <div className="relative flex items-center gap-1">
          <button
            onClick={openHistory}
            title="View version history"
            className="w-6 h-6 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
          >
            <IcHistory />
          </button>
          <button
            onClick={onEdit}
            disabled={!canEdit}
            title={canEdit ? "Edit this note" : "Editing this note is restricted by clinic policy"}
            className="w-6 h-6 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors disabled:opacity-30 disabled:hover:bg-transparent"
          >
            <IcPencil />
          </button>
          <button
            onClick={onDelete}
            disabled={!canEdit || isDeleting}
            title={canEdit ? "Delete this note" : "Deleting this note is restricted by clinic policy"}
            className="w-6 h-6 flex items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-500 transition-colors disabled:opacity-30 disabled:hover:bg-transparent"
          >
            <IcTrash />
          </button>
          {historyOpen && (
            <HistoryPopover history={history} loading={historyLoading} onClose={() => setHistoryOpen(false)} />
          )}
        </div>
      </div>
      <p className="text-sm text-slate-700 whitespace-pre-wrap">{note.body}</p>
    </div>
  );
};

// ─── Panel ────────────────────────────────────────────────────────────────────

interface DoctorNotesPanelProps {
  patientId: string;
  /** Visit context to attribute a newly-added note to (drives primary_doctor_only policy). */
  visitId?: string;
}

const DoctorNotesPanel: React.FC<DoctorNotesPanelProps> = ({ patientId, visitId }) => {
  const [notes, setNotes] = useState<NoteResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingEntryId, setEditingEntryId] = useState<string | null>(null);
  const [formSaving, setFormSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [deletingEntryId, setDeletingEntryId] = useState<string | null>(null);

  const { isAdmin, isDoctor, can_prescribe, isLoaded } = useRole();
  const policy = useNoteEditPolicy();

  // Doctor.doctor_id is a fresh UUID for normally-created doctors — it only
  // equals user_id for the founder/solo-onboarding legacy case. author_doctor_id
  // on a note is always the real doctor_id, so "is this my own note" must
  // compare against the real doctor_id too, not the cached auth user_id.
  const [currentDoctorId, setCurrentDoctorId] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    OnboardingAPI.getMe()
      .then(me => { if (!cancelled) setCurrentDoctorId(me.doctor_id ?? null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Eligible to write notes at all — mirrors the backend's require_clinical_credential
  // gate, which checks credential status (can_prescribe), not the role string. A
  // founder account can be role=Admin but still hold a Doctor row + verified
  // credentials (StartVisitPage.tsx uses this same (isDoctor || isAdmin) && can_prescribe
  // shape for the same reason) — gating on isDoctor alone would hide the Add button
  // for that legitimate case.
  const canWriteNotes = isLoaded && (isDoctor || isAdmin) && can_prescribe;

  // Client-side convenience gate only — the server is authoritative and
  // re-checks on every write. Under primary_doctor_only we can't always know
  // whether the acting doctor is the visit's primary doctor (that mapping
  // isn't in the feed payload), so this falls back to "is the current head's
  // author me" — correct for not-yet-edited entries and the common
  // just-added-my-own-note case; a false negative here just shows a
  // disabled button the server would actually have allowed. No blanket Admin
  // bypass here — the backend has none either, only policy/author matching.
  const canEditNote = useCallback(
    (note: NoteResponse): boolean => {
      if (!canWriteNotes) return false;
      if (policy === "any_clinic_doctor") return true;
      return currentDoctorId !== null && note.author_doctor_id === currentDoctorId;
    },
    [canWriteNotes, policy, currentDoctorId]
  );

  const load = useCallback(async () => {
    if (!patientId) {
      setLoading(false);
      return;
    }
    try {
      const data = await NotesAPI.list(patientId);
      setNotes(data);
      setFetchError(null);
    } catch (e) {
      setFetchError(e instanceof NoteApiError ? e.message : "Failed to load notes");
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAdd = async (body: string) => {
    setFormSaving(true);
    setFormError(null);
    try {
      const created = await NotesAPI.add(patientId, { body, visit_id: visitId });
      setNotes(prev => [created, ...prev]);
      setShowAddForm(false);
    } catch (e) {
      setFormError(e instanceof NoteApiError ? e.message : "Failed to add note");
    } finally {
      setFormSaving(false);
    }
  };

  const handleEdit = async (entryId: string, body: string) => {
    const current = notes.find(n => n.entry_id === entryId);
    if (!current) return;
    setFormSaving(true);
    setFormError(null);
    try {
      const updated = await NotesAPI.edit(patientId, entryId, { body, base_note_id: current.note_id });
      setNotes(prev => prev.map(n => (n.entry_id === entryId ? updated : n)));
      setEditingEntryId(null);
    } catch (e) {
      if (e instanceof NoteApiError && e.isConflict) {
        // Someone else edited this entry first — refresh to the real head
        // so the doctor can see what changed before retrying.
        try {
          const history = await NotesAPI.history(patientId, entryId);
          const latest = history.versions[0];
          if (latest) setNotes(prev => prev.map(n => (n.entry_id === entryId ? latest : n)));
        } catch { /* keep stale local copy — user can still cancel */ }
        setFormError("This note was edited by someone else. Showing the latest version — review and save again if needed.");
      } else {
        setFormError(e instanceof NoteApiError ? e.message : "Failed to save edit");
      }
    } finally {
      setFormSaving(false);
    }
  };

  const handleDelete = async (entryId: string) => {
    setDeletingEntryId(entryId);
    try {
      await NotesAPI.remove(patientId, entryId);
      setNotes(prev => prev.filter(n => n.entry_id !== entryId));
    } catch (e) {
      toast.error(e instanceof NoteApiError ? e.message : "Failed to delete note");
    } finally {
      setDeletingEntryId(null);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm px-4 pt-4 pb-3">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-sky-50 flex items-center justify-center text-sky-500">
            <IcNotebook />
          </div>
          <h2 className="text-[15px] font-bold text-slate-900">Doctor Notes</h2>
          {notes.length > 0 && (
            <span className="text-xs font-semibold text-slate-500 bg-slate-100 rounded-full px-2 py-0.5 leading-none">
              {notes.length}
            </span>
          )}
        </div>
        {canWriteNotes && !showAddForm && !editingEntryId && (
          <button
            onClick={() => setShowAddForm(true)}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-xl text-white text-sm font-semibold shadow-sm hover:opacity-90 transition-opacity bg-gradient-to-br from-blue-500 to-sky-400"
          >
            <IcPlus /> Add note
          </button>
        )}
      </div>

      {/* Loading / error states */}
      {loading && <p className="text-xs text-slate-400 text-center py-4">Loading…</p>}
      {!loading && fetchError && <p className="text-xs text-red-500 text-center py-4">{fetchError}</p>}

      {/* Feed */}
      {!loading && !fetchError && notes.length === 0 && !showAddForm && (
        <p className="text-xs text-slate-400 text-center py-4">No notes recorded yet</p>
      )}

      {!loading && !fetchError && notes.map(note => (
        <div key={note.entry_id}>
          {editingEntryId === note.entry_id ? (
            <div className="py-2">
              <NoteForm
                initialBody={note.body}
                onSave={body => handleEdit(note.entry_id, body)}
                onCancel={() => { setEditingEntryId(null); setFormError(null); }}
                saving={formSaving}
                error={formError}
              />
            </div>
          ) : (
            <NoteRow
              note={note}
              canEdit={canEditNote(note)}
              onEdit={() => { setShowAddForm(false); setFormError(null); setEditingEntryId(note.entry_id); }}
              onDelete={() => handleDelete(note.entry_id)}
              isDeleting={deletingEntryId === note.entry_id}
            />
          )}
        </div>
      ))}

      {/* Add form */}
      {showAddForm && (
        <NoteForm
          initialBody=""
          onSave={handleAdd}
          onCancel={() => { setShowAddForm(false); setFormError(null); }}
          saving={formSaving}
          error={formError}
        />
      )}
    </div>
  );
};

export default DoctorNotesPanel;
